// GROW LAB reference content — pH, nutrients, indoor grow media, outdoor soil
// Bilingual EN/ES, used by /grow-lab page.

export const PH_SCALE = {
    en: {
        title: "pH Scale Decoded",
        intro:
            "pH measures how acidic (low) or basic/alkaline (high) your water or soil is — on a 0–14 scale, where 7 is neutral. Most living systems have a sweet spot, and missing it means plants stop eating and fish stop breathing right.",
        bands: [
            { range: "0–4", label: "Strong Acid", color: "#ff3366", examples: "battery acid, lemon juice" },
            { range: "4–5.5", label: "Acidic", color: "#ff6a3d", examples: "vinegar, blueberries' happy zone" },
            { range: "5.5–6.5", label: "Slightly Acidic", color: "#ffaa00", examples: "best for many hydroponic plants" },
            { range: "6.5–7.5", label: "Neutral Sweet Spot", color: "#d4ff00", examples: "AQUAPONICS GOAL — pH 6.8–7.2" },
            { range: "7.5–8.5", label: "Slightly Basic", color: "#00f0ff", examples: "seawater, eggs" },
            { range: "8.5–14", label: "Strong Base", color: "#7a5cff", examples: "ammonia, drain cleaner" },
        ],
        why: [
            "pH < 6.5 → plants can't absorb iron, calcium, magnesium → yellow leaves",
            "pH > 7.5 → fish stress, ammonia becomes more toxic, bacteria slow down",
            "Aquaponics target: 6.8–7.2 (a compromise between fish, bacteria, plants)",
        ],
        fix: [
            "To LOWER pH: add a few drops of phosphoric acid OR more CO₂",
            "To RAISE pH: add crushed coral, eggshells, or calcium hydroxide (slow + safe)",
            "NEVER change pH by more than 0.2 / day — fish need time",
        ],
    },
    es: {
        title: "Escala de pH Decodificada",
        intro:
            "El pH mide qué tan ácida (bajo) o básica/alcalina (alto) es tu agua o suelo — escala 0–14, donde 7 es neutro. Si te sales del rango ideal, las plantas dejan de comer y los peces de respirar bien.",
        bands: [
            { range: "0–4", label: "Ácido Fuerte", color: "#ff3366", examples: "ácido de batería, jugo de limón" },
            { range: "4–5.5", label: "Ácido", color: "#ff6a3d", examples: "vinagre, ideal para arándanos" },
            { range: "5.5–6.5", label: "Ligeramente Ácido", color: "#ffaa00", examples: "ideal para muchas plantas hidropónicas" },
            { range: "6.5–7.5", label: "Zona Neutra Ideal", color: "#d4ff00", examples: "META ACUAPÓNICA — pH 6.8–7.2" },
            { range: "7.5–8.5", label: "Ligeramente Básico", color: "#00f0ff", examples: "agua marina, huevos" },
            { range: "8.5–14", label: "Base Fuerte", color: "#7a5cff", examples: "amoniaco, limpiadores" },
        ],
        why: [
            "pH < 6.5 → las plantas no absorben hierro, calcio, magnesio → hojas amarillas",
            "pH > 7.5 → estrés en peces, amoniaco más tóxico, bacterias lentas",
            "Meta acuapónica: 6.8–7.2 (equilibrio entre peces, bacterias y plantas)",
        ],
        fix: [
            "Para BAJAR pH: ácido fosfórico (pocas gotas) o más CO₂",
            "Para SUBIR pH: coral triturado, cáscara de huevo, hidróxido de calcio",
            "NUNCA cambies el pH más de 0.2 por día — los peces necesitan adaptarse",
        ],
    },
};

export const NUTRIENTS = {
    en: {
        title: "Plant Nutrient Cheat Sheet",
        macros: [
            {
                symbol: "N",
                name: "Nitrogen",
                role: "Leaf + stem growth (the 'green').",
                source: "Fish waste → nitrates",
                deficiency: "Yellowing on OLDER, lower leaves first",
                color: "#d4ff00",
            },
            {
                symbol: "P",
                name: "Phosphorus",
                role: "Roots, flowers, fruit set.",
                source: "Fish food breakdown, rock phosphate",
                deficiency: "Purple leaf undersides, stunted",
                color: "#ff6a3d",
            },
            {
                symbol: "K",
                name: "Potassium",
                role: "Water regulation, disease resistance, fruit quality.",
                source: "Kelp meal, banana peels (composted)",
                deficiency: "Browning leaf edges, weak stems",
                color: "#00f0ff",
            },
        ],
        micros: [
            { symbol: "Ca", name: "Calcium", note: "Cell walls. Deficit = blossom-end rot in tomatoes." },
            { symbol: "Mg", name: "Magnesium", note: "Center of chlorophyll. Add Epsom salt if pale between veins." },
            { symbol: "Fe", name: "Iron", note: "Locked out at high pH. New leaves go pale yellow." },
            { symbol: "S", name: "Sulfur", note: "Protein building. Rarely deficient." },
            { symbol: "Zn", name: "Zinc", note: "Growth hormones. Stunted, twisted new leaves." },
        ],
        tip:
            "In aquaponics, plants get ~95% of what they need from fish waste. The most common gap: IRON. Add chelated iron 1× a month.",
    },
    es: {
        title: "Guía Rápida de Nutrientes",
        macros: [
            {
                symbol: "N",
                name: "Nitrógeno",
                role: "Crecimiento de hojas y tallos (el 'verde').",
                source: "Desechos de peces → nitratos",
                deficiency: "Amarillamiento en hojas VIEJAS primero",
                color: "#d4ff00",
            },
            {
                symbol: "P",
                name: "Fósforo",
                role: "Raíces, flores, frutos.",
                source: "Comida de peces, roca fosfórica",
                deficiency: "Reverso púrpura, plantas pequeñas",
                color: "#ff6a3d",
            },
            {
                symbol: "K",
                name: "Potasio",
                role: "Resistencia a enfermedades, calidad del fruto.",
                source: "Algas, cáscara de plátano compostada",
                deficiency: "Bordes marrones, tallos débiles",
                color: "#00f0ff",
            },
        ],
        micros: [
            { symbol: "Ca", name: "Calcio", note: "Paredes celulares. Falta = pudrición apical del tomate." },
            { symbol: "Mg", name: "Magnesio", note: "Clorofila. Sales de Epsom si hay palidez entre venas." },
            { symbol: "Fe", name: "Hierro", note: "Bloqueado con pH alto. Hojas nuevas amarillas." },
            { symbol: "S", name: "Azufre", note: "Construcción de proteínas. Rara vez falta." },
            { symbol: "Zn", name: "Zinc", note: "Hormonas. Hojas nuevas pequeñas y torcidas." },
        ],
        tip:
            "En acuaponía las plantas obtienen ~95% de lo que necesitan de los peces. La falta más común: HIERRO. Agrega hierro quelado 1×/mes.",
    },
};

export const GROW_MEDIA = {
    en: {
        title: "Indoor Grow Media",
        subtitle: "What roots grow IN inside an aquaponics or hydroponics system.",
        items: [
            {
                name: "Clay Pebbles (LECA / Hydroton)",
                pros: ["Reusable forever", "Holds water + air", "Light, easy to handle", "pH neutral"],
                cons: ["Pricey upfront ($25-40 / bag)", "Floats when new — rinse first"],
                best: "Media beds, beginner builds",
                cost: "$$",
                reuse: "Yes — rinse + bake",
                ph: "Neutral (6.5-7.0)",
                color: "#ff6a3d",
            },
            {
                name: "Gravel / River Rock",
                pros: ["Cheapest option", "Heavy = stable", "Plenty of surface for bacteria"],
                cons: ["HEAVY (back-breaking)", "Some types raise pH (limestone)", "Sharper on roots"],
                best: "Big media beds, low budget",
                cost: "$",
                reuse: "Yes — easy",
                ph: "Test first (limestone = high)",
                color: "#a0a4b8",
            },
            {
                name: "Perlite",
                pros: ["Super light", "Great drainage", "Cheap"],
                cons: ["Floats away", "Dusty (wear a mask)"],
                best: "Mixed with coco coir, seed-starts",
                cost: "$",
                reuse: "Limited",
                ph: "Neutral",
                color: "#f0f0f0",
            },
            {
                name: "Coco Coir",
                pros: ["Renewable (coconut husk)", "Holds moisture well", "Organic"],
                cons: ["Compacts over time", "Needs flushing — high salts"],
                best: "Seed starts, mixed with perlite",
                cost: "$$",
                reuse: "One season usually",
                ph: "Slightly acidic (5.5-6.5)",
                color: "#7a5230",
            },
            {
                name: "Rockwool",
                pros: ["Sterile", "Perfect for seedlings", "Holds water + air"],
                cons: ["Not eco-friendly (mineral fiber)", "Dust = lung hazard"],
                best: "Commercial hydroponics, seedlings",
                cost: "$$",
                reuse: "One use",
                ph: "Alkaline — pre-soak in acid water",
                color: "#d4ff00",
            },
            {
                name: "Lava Rock",
                pros: ["Free / cheap from quarries", "Huge surface for bacteria", "Doesn't break down"],
                cons: ["Sharp edges on roots", "Heavy"],
                best: "DIY budget systems",
                cost: "$",
                reuse: "Yes",
                ph: "Usually neutral",
                color: "#5a2c2c",
            },
        ],
    },
    es: {
        title: "Sustratos para Interior",
        subtitle: "Dónde crecen las raíces en sistemas acuapónicos / hidropónicos.",
        items: [
            {
                name: "Arcilla Expandida (Hydroton)",
                pros: ["Reutilizable", "Retiene agua y aire", "Liviana", "pH neutro"],
                cons: ["Costosa al inicio", "Flota nueva — enjuaga primero"],
                best: "Camas de sustrato, principiantes",
                cost: "$$",
                reuse: "Sí — enjuaga y hornea",
                ph: "Neutro (6.5-7.0)",
                color: "#ff6a3d",
            },
            {
                name: "Grava / Piedra de Río",
                pros: ["Más barato", "Pesada = estable", "Mucha superficie para bacterias"],
                cons: ["PESADA", "Algunas suben el pH (caliza)", "Filos en raíces"],
                best: "Camas grandes, presupuesto bajo",
                cost: "$",
                reuse: "Sí — fácil",
                ph: "Verifica (caliza = alto)",
                color: "#a0a4b8",
            },
            {
                name: "Perlita",
                pros: ["Muy ligera", "Excelente drenaje", "Barata"],
                cons: ["Flota", "Polvosa (usa máscara)"],
                best: "Mezclada con fibra de coco, plántulas",
                cost: "$",
                reuse: "Limitado",
                ph: "Neutro",
                color: "#f0f0f0",
            },
            {
                name: "Fibra de Coco",
                pros: ["Renovable", "Retiene humedad", "Orgánica"],
                cons: ["Se compacta", "Necesita lavado — sales altas"],
                best: "Plántulas, mezclada con perlita",
                cost: "$$",
                reuse: "Una temporada",
                ph: "Ligeramente ácido (5.5-6.5)",
                color: "#7a5230",
            },
            {
                name: "Lana de Roca",
                pros: ["Estéril", "Perfecta para plántulas", "Retiene agua y aire"],
                cons: ["No ecológica", "Polvo = riesgo pulmonar"],
                best: "Hidroponía comercial",
                cost: "$$",
                reuse: "Una vez",
                ph: "Alcalina — pre-empapa en ácido",
                color: "#d4ff00",
            },
            {
                name: "Roca Volcánica",
                pros: ["Gratis/barata en canteras", "Mucha superficie", "No se degrada"],
                cons: ["Filos en raíces", "Pesada"],
                best: "Sistemas DIY económicos",
                cost: "$",
                reuse: "Sí",
                ph: "Usualmente neutro",
                color: "#5a2c2c",
            },
        ],
    },
};

export const SOIL_TYPES = {
    en: {
        title: "Outdoor Soil Types",
        subtitle:
            "If you're taking it from the tank to the lot, rooftop, or backyard — know your dirt.",
        items: [
            {
                name: "Sandy",
                feel: "Gritty, falls through fingers",
                drainage: "Very fast (sometimes too fast)",
                fertility: "Low — nutrients wash out",
                ph: "Often slightly acidic",
                best: "Carrots, radishes, herbs",
                fix: "Add compost + mulch to hold moisture",
                color: "#e6c97a",
            },
            {
                name: "Silty",
                feel: "Smooth, soapy when wet",
                drainage: "Moderate",
                fertility: "High — holds nutrients well",
                ph: "Usually neutral",
                best: "Most vegetables, lettuce, kale",
                fix: "Add organic matter to prevent compaction",
                color: "#c7a978",
            },
            {
                name: "Clay",
                feel: "Sticky when wet, rock-hard when dry",
                drainage: "Slow — risk of root rot",
                fertility: "High but locked up",
                ph: "Variable",
                best: "Tough plants — collards, kale, broccoli",
                fix: "Mix in compost, sand, gypsum to loosen",
                color: "#a85230",
            },
            {
                name: "Loamy",
                feel: "Soft, crumbly, smells like a forest",
                drainage: "Perfect (the goal)",
                fertility: "High and balanced",
                ph: "Near neutral",
                best: "EVERYTHING — the gold standard",
                fix: "Maintain with compost + cover crops",
                color: "#5a3a1a",
            },
            {
                name: "Peaty",
                feel: "Dark, spongy, smells damp",
                drainage: "Holds too much water",
                fertility: "Acidic — locks some nutrients",
                ph: "Acidic (4–5)",
                best: "Blueberries, potatoes",
                fix: "Lime to raise pH, drainage holes",
                color: "#2a1a0a",
            },
            {
                name: "Chalky",
                feel: "Stony, light-colored",
                drainage: "Fast",
                fertility: "Low — iron + manganese locked out",
                ph: "Alkaline (7.5+)",
                best: "Spinach, cabbage, beets",
                fix: "Lots of compost; iron supplements",
                color: "#dcdcc7",
            },
        ],
        urbanNote:
            "Urban lot soil is often contaminated (lead, old paint). ALWAYS test a city plot before eating from it — your county extension office often does this free. Raised beds with imported soil are the safest move.",
    },
    es: {
        title: "Tipos de Suelo (Exterior)",
        subtitle:
            "Si llevas el conocimiento del tanque al lote, azotea o jardín — conoce tu tierra.",
        items: [
            {
                name: "Arenoso",
                feel: "Áspero, se escurre entre los dedos",
                drainage: "Muy rápido",
                fertility: "Baja — los nutrientes se lavan",
                ph: "Ligeramente ácido",
                best: "Zanahorias, rábanos, hierbas",
                fix: "Compost + mantillo para retener humedad",
                color: "#e6c97a",
            },
            {
                name: "Limoso",
                feel: "Suave, jabonoso cuando mojado",
                drainage: "Moderado",
                fertility: "Alta — retiene nutrientes",
                ph: "Neutro",
                best: "La mayoría de vegetales",
                fix: "Materia orgánica para evitar compactación",
                color: "#c7a978",
            },
            {
                name: "Arcilloso",
                feel: "Pegajoso mojado, durísimo seco",
                drainage: "Lento — riesgo de pudrición",
                fertility: "Alta pero bloqueada",
                ph: "Variable",
                best: "Plantas resistentes — coles",
                fix: "Compost, arena, yeso para soltar",
                color: "#a85230",
            },
            {
                name: "Franco (Loam)",
                feel: "Suave, desmoronable, huele a bosque",
                drainage: "Perfecto (la meta)",
                fertility: "Alta y balanceada",
                ph: "Casi neutro",
                best: "TODO — el estándar de oro",
                fix: "Mantén con compost y cultivos de cobertura",
                color: "#5a3a1a",
            },
            {
                name: "Turba",
                feel: "Oscuro, esponjoso, huele a humedad",
                drainage: "Retiene demasiada agua",
                fertility: "Ácida — bloquea nutrientes",
                ph: "Ácido (4–5)",
                best: "Arándanos, papas",
                fix: "Cal para subir pH, drenaje",
                color: "#2a1a0a",
            },
            {
                name: "Calcáreo",
                feel: "Pedregoso, color claro",
                drainage: "Rápido",
                fertility: "Baja — hierro/manganeso bloqueados",
                ph: "Alcalino (7.5+)",
                best: "Espinaca, repollo, betabel",
                fix: "Mucho compost, suplementos de hierro",
                color: "#dcdcc7",
            },
        ],
        urbanNote:
            "El suelo urbano suele estar contaminado (plomo, pintura vieja). SIEMPRE prueba antes de comer lo cultivado — la extensión del condado lo hace gratis. Camas elevadas con tierra importada son lo más seguro.",
    },
};
